/**
 * Teams channel tab configuration page.
 * Shown when ?teamsConfig=1 is in the URL.
 * Lets the channel admin pick a view (and optionally a project) for the tab.
 */
import React,{useState,useEffect}from"react";
import{pages,app}from"@microsoft/teams-js";
import{BLUE,GOLD}from"../config";

const VIEWS=[
  {id:"dashboard",label:"Dashboard","desc":"Stats overview and task highlights"},
  {id:"company",label:"Company Tasks","desc":"All company-wide tasks"},
  {id:"myday",label:"My Day","desc":"Each user sees their own tasks for today"},
  {id:"workqueue",label:"Work Queue","desc":"Priority-ordered task queue"},
];

export default function TeamsConfig({projects=[]}){
  const[selectedView,setSelectedView]=useState("company");
  const[selectedProject,setSelectedProject]=useState("");
  const[ready,setReady]=useState(false);

  useEffect(()=>{
    app.initialize().then(()=>{
      setReady(true);
      pages.config.registerOnSaveHandler(saveEvent=>{
        const params=new URLSearchParams();
        params.set("view",selectedView);
        if(selectedProject) params.set("project",selectedProject);
        const contentUrl=`https://taskhub.dacrobotics.com?${params.toString()}`;
        pages.config.setConfig({
          suggestedDisplayName:"TaskHub",
          entityId:`taskhub-${selectedView}${selectedProject?`-${selectedProject}`:""}`,
          contentUrl,
          websiteUrl:"https://taskhub.dacrobotics.com",
        });
        saveEvent.notifySuccess();
      });
    }).catch(()=>setReady(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(()=>{
    if(!ready) return;
    try{pages.config.setValidityState(true);}catch{}
  },[ready,selectedView,selectedProject]);

  const activeProjects=(projects||[]).filter(p=>p.isActive);
  const customerProjects=activeProjects.filter(p=>p.projectType==="customer")
    .sort((a,b)=>(a.customerName||"").localeCompare(b.customerName||"")||
      (a.jobNumber||"").localeCompare(b.jobNumber||"",undefined,{numeric:true}));
  const internalProjects=activeProjects.filter(p=>p.projectType==="internal")
    .sort((a,b)=>(a.projectName||"").localeCompare(b.projectName||""));

  return(
    <div className="min-h-screen bg-slate-50 p-6" style={{fontFamily:"system-ui,sans-serif"}}>
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-lg" style={{background:BLUE}}>T</div>
          <div>
            <h1 className="text-lg font-bold text-slate-800">Configure TaskHub Tab</h1>
            <p className="text-xs text-slate-500">Choose what this channel tab shows</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">View</p>
          <div className="space-y-2">
            {VIEWS.map(v=>(
              <label key={v.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                ${selectedView===v.id?"border-blue-400 bg-blue-50":"border-slate-200 hover:border-slate-300"}`}>
                <input type="radio" name="view" value={v.id} checked={selectedView===v.id}
                  onChange={()=>setSelectedView(v.id)} className="mt-0.5 accent-blue-600"/>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{v.label}</p>
                  <p className="text-xs text-slate-500">{v.desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {selectedView==="company"&&activeProjects.length>0&&(
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Filter by Project <span className="text-slate-300 font-normal normal-case tracking-normal">(optional)</span></p>
            <select value={selectedProject} onChange={e=>setSelectedProject(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-white">
              <option value="">All company tasks</option>
              {customerProjects.length>0&&(
                <optgroup label="Customer Projects">
                  {customerProjects.map(p=>(
                    <option key={p.spId||p.id} value={String(p.spId||p.id)}>
                      {p.customerName?`${p.customerName}${p.jobNumber?` · ${p.jobNumber}`:""} – `:""}{p.projectName}
                    </option>
                  ))}
                </optgroup>
              )}
              {internalProjects.length>0&&(
                <optgroup label="Internal Projects">
                  {internalProjects.map(p=>(
                    <option key={p.spId||p.id} value={String(p.spId||p.id)}>{p.projectName}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
        )}

        <p className="text-[11px] text-slate-400 mt-4 text-center">
          Click <strong>Save</strong> in Teams to add this tab to the channel.
        </p>
      </div>
    </div>
  );
}
